import '@yandex-int/messenger.widget/lib/ui/block.css';
import {
    authHelperPluginFactory,
    Block,
    blockUIFactory,
    Widget,
    YandexSingleBlockChatConfig,
    YandexTeamSingleBlockChatConfig,
} from '@yandex-int/messenger.widget';
import { cn } from '@bem-react/classname';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { ChatProps } from './Chat.interface';

import './Chat.css';

type WidgetState = { ui: Block; widget: Widget };
type WidgetOptions = {
    flags: string[];
    chatType: string;
    inviteHash: string;
    userEmail: string;
};

const cnChat = cn('Chat');

const IS_TEST = process.env.NODE_ENV === 'test';

export const Chat = (props: ChatProps): ReactElement => {
    const {
        chatInviteHash: defaultChatInviteHash,
        chatType: defaultChatType,
        className,
        flags = [],
        userEmail,
    } = props;

    const { chatType, chatInviteHash } = useMemo(() => {
        const {
            chatType: forcedChatType,
            chatInviteHash: forcedChatInviteHash,
        } = getForcedChatValues();

        const chatType = forcedChatType || defaultChatType;
        const chatInviteHash = forcedChatInviteHash || defaultChatInviteHash;

        return { chatType, chatInviteHash };
    }, [defaultChatType, defaultChatInviteHash]);

    const [widgetState] = useState(() => {
        return createWidget({ chatType, inviteHash: chatInviteHash, flags, userEmail });
    });

    const chatRef = useRef<HTMLDivElement>();

    const { ui, widget } = widgetState;

    useEffect(() => {
        if (!chatRef.current || IS_TEST) {
            return;
        }

        ui.mount(chatRef.current);

        return () => {
            widget.destroy();
        };
    }, [ui, widget]);

    useEffect(() => {
        if (IS_TEST) {
            return;
        }

        widget.show({
            inviteHash: chatInviteHash,
        });
    }, [widget, chatInviteHash]);

    return (
        <div ref={chatRef} className={cnChat(null, [className])} />
    );
};

export default Chat;

const getForcedChatValues = ():
  | { chatType: string | undefined; chatInviteHash: string | undefined }
  | undefined => {
    try {
        const params = new URLSearchParams(location.search);
        return {
            chatType: params.get('_yambChatType'),
            chatInviteHash: params.get('_yambChatInviteHash'),
        };
    } catch {
        return undefined;
    }
};

const createWidget = (options: WidgetOptions): WidgetState => {
    const { flags, chatType, inviteHash, userEmail } = options;

    const flagsObj: Record<string, string> = {
        disableChatList: '0',
    };
    flags.forEach(flag => {
        flagsObj[flag] = '1';
    });

    const ConfigConstructor =
          chatType === 'yandex' ? YandexSingleBlockChatConfig : YandexTeamSingleBlockChatConfig;

    const widget = new Widget(new ConfigConstructor({
        serviceId: 181,
        iframeOpenData: {
            inviteHash,
        },
        flags: flagsObj,
        selectedLogin: userEmail,
    }));

    const ui = blockUIFactory();
    widget.setUI(ui);

    const authHelper = authHelperPluginFactory('vconf');
    authHelper.onError.addListener(error => {
        logError(error, 'messengerError');
    });
    authHelper.onAuthorizationNeeded.addListener(() => {
        logError(new Error('authNeeded'), 'messengerAuth');
    });

    widget.addPlugin(authHelper);

    widget.init();

    return { ui, widget };
};

export const logError = (error: Error, method: string, additional: Object = {}) => {
    console.error(error);
    console.error('additional:', additional);

    const env = process.env.NODE_ENV === 'test' ? 'testing' : process.env.NODE_ENV;
    const version = process.env.VERSION;

    if (window?.Ya?.Rum?.logError) {
        window.Ya.Rum.logError({
            message: error.message,
            block: 'messenger',
            method: method,
            page: 'messenger',
            service: 'vconf',
            level: Ya.Rum.ERROR_LEVEL.ERROR,
            ignoreErrorMessage: true,
            additional,
        }, error, { env, version });
    }
};
